import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChartBarIcon,
  DocumentTextIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

/** Converts option text(s) to a,b,c format for MCQ display */
function formatUserAnswerAsLetters(userAnswer, options) {
  if (!options || !Array.isArray(options) || options.length === 0) return null;
  const answers = Array.isArray(userAnswer) ? userAnswer : userAnswer ? [userAnswer] : [];
  if (answers.length === 0) return null;
  const letters = answers
    .map((text) => {
      const idx = options.findIndex((opt) => opt?.text?.trim() === String(text).trim());
      return idx >= 0 ? String.fromCharCode(97 + idx) : null;
    })
    .filter(Boolean);
  return letters.length > 0 ? letters.join(', ') : null;
}

/** Correct answer as a,b,c for MCQ; otherwise raw value */
function formatCorrectAnswerAsLetters(correctAnswer, options) {
  if (options && Array.isArray(options) && options.length > 0) {
    const letters = options
      .map((opt, idx) => (opt?.isCorrect ? String.fromCharCode(97 + idx) : null))
      .filter(Boolean);
    if (letters.length > 0) return letters.join(', ');
  }
  return correctAnswer;
}

function formatRenderableAnswer(answer) {
  if (answer == null || answer === '') return 'No answer';
  if (Array.isArray(answer)) return answer.join(', ');
  if (typeof answer === 'object') {
    if (typeof answer.code === 'string') return answer.code || 'No answer';
    if (typeof answer.text === 'string') return answer.text || 'No answer';
    return JSON.stringify(answer);
  }
  return answer;
}

const CandidateDetailedResults = () => {
  const { id: assessmentId, candidateEmail } = useParams();
  const navigate = useNavigate();

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCandidateResults();
  }, [assessmentId, candidateEmail]);

  const fetchCandidateResults = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `/api/assessments/${assessmentId}/results/${candidateEmail}`
      );
      setResults(response.data);
    } catch (error) {
      console.error('Error fetching candidate results:', error);
      if (error.response?.status === 404) {
        setError('Candidate results not found');
      } else {
        setError('Failed to load candidate results');
      }
    } finally {
      setLoading(false);
    }
  }, [assessmentId, candidateEmail]);

  const formatTime = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <XCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Error</h1>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate(`/dashboard/assessments/${assessmentId}/results`)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Back to Results
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-gray-600">No results available</p>
          </div>
        </div>
      </div>
    );
  }

  const { candidate, assessment, results: questionResults } = results;
  const totalQuestions = questionResults.length;
  const correctAnswers = questionResults.filter((r) => r.isCorrect).length;
  const percentage = totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(`/dashboard/assessments/${assessmentId}/results`)}
              className="flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to Results
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Candidate Results</h1>
              <p className="text-gray-600">{assessment.title}</p>
            </div>
          </div>
        </div>

        {/* Candidate Summary */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Candidate Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center text-sm text-gray-600">
              <UserIcon className="h-4 w-4 mr-2" />
              <span>
                <strong>Name:</strong> {candidate.name || 'No Name'}
              </span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <DocumentTextIcon className="h-4 w-4 mr-2" />
              <span>
                <strong>Email:</strong> {candidate.email}
              </span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <ClockIcon className="h-4 w-4 mr-2" />
              <span>
                <strong>Time Spent:</strong> {formatTime(candidate.timeSpent)}
              </span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <ChartBarIcon className="h-4 w-4 mr-2" />
              <span>
                <strong>Score:</strong> {candidate.score}/{assessment.totalPoints}
              </span>
            </div>
          </div>
        </div>

        {/* Performance Overview */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Performance Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{totalQuestions}</div>
              <div className="text-sm text-gray-600">Total Questions</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-700">{correctAnswers}</div>
              <div className="text-sm text-green-600">Correct Answers</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-700">
                {totalQuestions - correctAnswers}
              </div>
              <div className="text-sm text-red-600">Incorrect Answers</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-700">{percentage.toFixed(1)}%</div>
              <div className="text-sm text-blue-600">Success Rate</div>
            </div>
          </div>
        </div>

        {/* Question-by-Question Results */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Question Results</h2>
          </div>

          <div className="divide-y divide-gray-200">
            {questionResults.map((result, index) => (
              <div key={index} className={`p-6 ${result.isCorrect ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <span className="text-lg font-medium text-gray-900">
                      Question {result.questionNumber}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      {result.points} point{result.points !== 1 ? 's' : ''}
                    </span>
                    {result.question.source === 'test' && (
                      <span className="text-xs text-gray-500">
                        From: {result.question.testTitle}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    {result.isCorrect ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircleIcon className="h-5 w-5 text-red-600" />
                    )}
                    <span
                      className={`text-sm font-medium ${
                        result.isCorrect ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {result.earnedPoints}/{result.points}
                    </span>
                  </div>
                </div>

                <div className="mb-3">
                  <h4 className="font-medium text-gray-900 mb-1">{result.question.title}</h4>
                  {result.question.content && (
                    <p className="text-sm text-gray-600">{result.question.content}</p>
                  )}

                  {/* Show question options for multiple choice questions */}
                  {result.question.options && result.question.options.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-gray-700 mb-1">Options:</p>
                      <div className="space-y-1">
                        {result.question.options.map((option, optIndex) => (
                          <div key={optIndex} className="flex items-center space-x-2 text-xs">
                            <span
                              className={`w-4 h-4 rounded flex items-center justify-center ${
                                option.isCorrect
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {String.fromCharCode(65 + optIndex)}
                            </span>
                            <span
                              className={`${
                                option.isCorrect ? 'text-green-700 font-medium' : 'text-gray-600'
                              }`}
                            >
                              {option.text}
                              {option.isCorrect && <span className="ml-1 text-green-600">✓</span>}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Your Answer: </span>
                    <span className={result.isCorrect ? 'text-green-600' : 'text-red-600'}>
                      {formatUserAnswerAsLetters(result.userAnswer, result.question?.options) ??
                        formatRenderableAnswer(result.userAnswer)}
                    </span>
                  </div>

                  <div>
                    <span className="font-medium text-gray-700">Points: </span>
                    <span className="text-gray-600">
                      {result.earnedPoints}/{result.points} earned
                    </span>
                  </div>

                  {result.correctAnswer && (
                    <div className="md:col-span-2">
                      <span className="font-medium text-gray-700">Correct Answer: </span>
                      <span className="text-green-600">
                        {formatCorrectAnswerAsLetters(
                          result.correctAnswer,
                          result.question?.options
                        )}
                      </span>
                    </div>
                  )}

                  {result.question.type && (
                    <div className="md:col-span-2">
                      <span className="font-medium text-gray-700">Question Type: </span>
                      <span className="text-gray-600 capitalize">{result.question.type}</span>
                    </div>
                  )}
                </div>

                {/* Passage Reading Results */}
                {result.question.type === 'Passage Reading' && result.passageReading && (
                  <div className="mt-4 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                    <h5 className="font-semibold text-indigo-900 mb-3">Pronunciation Analysis</h5>
                    
                    {/* Transcript */}
                    <div className="mb-4">
                      <span className="font-medium text-gray-700 block mb-1">Transcript:</span>
                      <p className="text-sm text-gray-600 bg-white p-3 rounded border border-gray-200">
                        {result.passageReading.transcript || 'No transcript available'}
                      </p>
                    </div>

                    {/* Scores */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                      <div className="bg-white p-2 rounded border border-gray-200 text-center">
                        <div className="text-lg font-bold text-indigo-600">
                          {result.passageReading.scores.overall}%
                        </div>
                        <div className="text-xs text-gray-600">Overall</div>
                      </div>
                      <div className="bg-white p-2 rounded border border-gray-200 text-center">
                        <div className="text-lg font-bold text-blue-600">
                          {result.passageReading.scores.accuracy}%
                        </div>
                        <div className="text-xs text-gray-600">Accuracy</div>
                      </div>
                      <div className="bg-white p-2 rounded border border-gray-200 text-center">
                        <div className="text-lg font-bold text-green-600">
                          {result.passageReading.scores.fluency}%
                        </div>
                        <div className="text-xs text-gray-600">Fluency</div>
                      </div>
                      <div className="bg-white p-2 rounded border border-gray-200 text-center">
                        <div className="text-lg font-bold text-purple-600">
                          {result.passageReading.scores.completeness}%
                        </div>
                        <div className="text-xs text-gray-600">Completeness</div>
                      </div>
                      <div className="bg-white p-2 rounded border border-gray-200 text-center">
                        <div className="text-lg font-bold text-orange-600">
                          {result.passageReading.scores.pronunciation}%
                        </div>
                        <div className="text-xs text-gray-600">Pronunciation</div>
                      </div>
                    </div>

                    {/* Feedback */}
                    {result.passageReading.feedback && (
                      <div className="space-y-2">
                        {result.passageReading.feedback.strengths && result.passageReading.feedback.strengths.length > 0 && (
                          <div>
                            <span className="font-medium text-green-700 block mb-1">Strengths:</span>
                            <ul className="text-sm text-gray-600 list-disc list-inside">
                              {result.passageReading.feedback.strengths.map((strength, idx) => (
                                <li key={idx}>{strength}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {result.passageReading.feedback.weaknesses && result.passageReading.feedback.weaknesses.length > 0 && (
                          <div>
                            <span className="font-medium text-red-700 block mb-1">Areas for Improvement:</span>
                            <ul className="text-sm text-gray-600 list-disc list-inside">
                              {result.passageReading.feedback.weaknesses.map((weakness, idx) => (
                                <li key={idx}>{weakness}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {result.passageReading.feedback.overallAssessment && (
                          <div>
                            <span className="font-medium text-gray-700 block mb-1">Overall Assessment:</span>
                            <p className="text-sm text-gray-600">{result.passageReading.feedback.overallAssessment}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateDetailedResults;
